import { useState, useEffect } from "react";
import { overallBetLimitsApi } from "../../api/overallBetLimits";
import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import { Modal, Button, Label, TextInput, Select } from "flowbite-react";
import { Plus, Trash2, X } from "lucide-react";
import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

export default function OverallBetLimits() {
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overallBetLimits, setOverallBetLimits] = useState([]);
  const [games, setGames] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [drawTimes, setDrawTimes] = useState([]);
  
  // Form data for adding a new overall bet limit
  const [formData, setFormData] = useState({
    gameId: "",
    drawTime: "",
    limit: 0
  });

  useEffect(() => {
    fetchOverallBetLimits();
    fetchGames();
  }, [page]);

  const convertTimeto12 = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  useEffect(() => {
    // Update draw times when game selection changes
    if (formData.gameId && games.length > 0) {
      const selectedGame = games.find(game => game.id === Number(formData.gameId));
      if (selectedGame && selectedGame.otherSettings && selectedGame.otherSettings.drawTime) {
        setDrawTimes(selectedGame.otherSettings.drawTime.map(convertTimeto12));
        // Reset draw time when game changes
        setFormData(prev => ({
          ...prev,
          drawTime: ""
        }));
      } else {
        setDrawTimes([]);
      }
    }
  }, [formData.gameId, games]);

  const fetchOverallBetLimits = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await overallBetLimitsApi.getOverallBetLimits(tokens.access.token, page, 20);
      setOverallBetLimits(data.rows || []);
      setTotalPages(Math.ceil((data.count || 0) / 20));
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch overall bet limits. Please try again.");
      setLoading(false);
      console.error(err);
    }
  };

  const fetchGames = async () => {
    try {
      const { data } = await gamesApi.getAllGames(tokens.access.token);
      setGames(data || []);

      // Set default game if available
      if (data && data.length > 0) {
        setFormData(prev => ({
          ...prev,
          gameId: data[0].id
        }));
      }
    } catch (err) {
      console.error("Failed to fetch games:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "limit" ? Number(value) : value
    });
  };

  const handleAddLimit = async () => {
    // Validate form
    if (!formData.gameId || !formData.drawTime || formData.limit <= 0) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill in all fields with valid values',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setLoading(true);
      await overallBetLimitsApi.createOverallBetLimit(tokens.access.token, formData);
      setShowAddModal(false);
      
      // Reset form
      setFormData({
        gameId: games.length > 0 ? games[0].id : "",
        drawTime: "",
        limit: 0
      });
      
      // Refresh the list
      fetchOverallBetLimits();
      
      Swal.fire({
        title: 'Success!',
        text: 'Overall bet limit added successfully',
        icon: 'success',
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to add overall bet limit',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
      setLoading(false);
    }
  };

  const handleDeleteLimit = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this overall bet limit!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await overallBetLimitsApi.deleteOverallBetLimit(tokens.access.token, id);
          
          // Refresh the list
          fetchOverallBetLimits();
          
          Swal.fire({
            title: 'Deleted!',
            text: 'The overall bet limit has been deleted.',
            icon: 'success',
            confirmButtonColor: '#3085d6'
          });
        } catch (err) {
          Swal.fire({
            title: 'Error!',
            text: err.message || 'Failed to delete overall bet limit',
            icon: 'error',
            confirmButtonColor: '#3085d6'
          });
          setLoading(false);
        }
      }
    });
  };

  return (
    <div className="bg-[#F8F9FB] min-h-screen pb-20">
      <HeaderWithIcon title="Overall Bet Limits" onBack={() => navigate(-1)} />
      
      <div className="px-4 py-3">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Overall Bet Limits</h2>
          <Button 
            color="blue" 
            onClick={() => setShowAddModal(true)}
            className="px-3 py-2"
          >
            Add Bet Limit
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-2 text-gray-600">Loading overall bet limits...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">{error}</div>
        ) : (
          <div className="space-y-3">
            {overallBetLimits.length > 0 ? (
              overallBetLimits.map((limit) => (
                <div 
                  key={limit.id} 
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Game</div>
                      <div className="font-semibold text-gray-900">{limit.GameSetting?.name || `Game ID: ${limit.gameId}`}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteLimit(limit.id)}
                      className="text-red-600 font-medium text-sm hover:underline"
                    >
                      DELETE
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Draw Time</div>
                      <div className="font-semibold text-gray-900">{limit.drawTime}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-700">Limit</div>
                      <div className="font-semibold text-gray-900">₱{limit.limit.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">No overall bet limits found</div>
            )}
          </div>
        )}
      </div>

      {/* Add Limit Modal */}
      <Modal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        popup
        size="md"
      >
        <Modal.Header className="px-6 pt-6 pb-0 flex justify-between items-center">
          <div></div>
          <button
            className="text-gray-500 hover:text-gray-900"
            onClick={() => setShowAddModal(false)}
          >
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="gameId" value="Game Type" />
              </div>
              <Select
                id="gameId"
                name="gameId"
                value={formData.gameId}
                onChange={handleInputChange}
                required
              >
                {games.map(game => (
                  <option key={game.id} value={game.id}>{game.name}</option>
                ))}
              </Select>
            </div>
            
            <div>
              <div className="mb-2 block">
                <Label htmlFor="drawTime" value="Select Draw Time" />
              </div>
              <Select
                id="drawTime"
                name="drawTime"
                value={formData.drawTime}
                onChange={handleInputChange}
                required
                disabled={drawTimes.length === 0}
              >
                <option value="" disabled>Select a draw time</option>
                {drawTimes.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </Select>
            </div>
            
            <div>
              <div className="mb-2 block">
                <Label htmlFor="limit" value="Limit" />
              </div>
              <TextInput
                id="limit"
                name="limit"
                type="number"
                min="0"
                value={formData.limit || ""}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="flex justify-end gap-4 mt-6">
              <Button 
                color="gray" 
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button 
                color="blue" 
                onClick={handleAddLimit}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
} 